/**
 * Copyright Mántum All Rights Reserved.
 */
import Button from '../button';
import Module from './module';
import SearchBox from '../search';
import Section from '../section';
import { ListBulletIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { accent, sort } from '@mantum/kanela';
import { useEffect, useState } from 'react';

import type { Contexts, Context, Option, Advanced, Group } from './type';

type PropTypes = {
    advanced?: Advanced;
    contexts?: Contexts;
    message?: string;
    onClick?: (value: Context | Option) => void;
    onClose?: () => void;
    placeholder?: string;
    selected?: Group;
    spaces?: number;
};

export default function Body(props: PropTypes) {
    // prettier-ignore
    const {
        contexts = [],
        message = 'No data',
        spaces = 2
    } = props;

    const [dataSource, setDataSource] = useState<Contexts>(contexts);

    useEffect(() => {
        if (contexts.length !== 0) {
            setDataSource(contexts);
        }
    }, [contexts]);

    useEffect(() => {
        if (!props.selected) {
            return;
        }

        setDataSource(
            contexts.filter((it) => {
                if (props.selected?.default) {
                    return it;
                }
                return it.group?.includes(props.selected!.key);
            })
        );
    }, [props.selected, contexts]);

    const onSearch = (_?: string, values?: unknown) => {
        let results = [];
        if (Array.isArray(values)) {
            results = props.selected
                ? values.filter((it) => {
                      if (props.selected?.default) {
                          return it;
                      }

                      return it.group
                          ?.map((it: string) => accent(it))
                          .includes(accent(props.selected!.name));
                  })
                : values;
        }

        setDataSource(results);
    };

    return (
        <div className='h-full overflow-hidden'>
            <div className='flex h-full flex-col md:max-w-2xl'>
                <div className='flex flex-row gap-4 p-4'>
                    <Button
                        className='rounded-md p-2 hover:bg-gray-300 md:hidden'
                        onClick={props.onClose}
                    >
                        <ChevronLeftIcon className='h-6 w-6' />
                    </Button>
                    <div className='w-full'>
                        <SearchBox
                            advanced={props.advanced?.search}
                            dataSource={contexts}
                            onChange={onSearch}
                            placeholder={props.placeholder}
                        />
                    </div>
                    <Button className='rounded-md p-2 hover:bg-gray-300 md:hidden'>
                        <ListBulletIcon className='h-6 w-6' />
                    </Button>
                </div>
                <div
                    className={`h-full overflow-y-auto px-4 ${
                        spaces === 1 ? 'md:min-w-[24rem]' : 'md:min-w-[32rem]'
                    }`}
                >
                    {dataSource.length === 0 && (
                        <div className='grid h-full items-center px-2'>
                            <p className='text-center text-lg text-gray-700'>
                                {message}
                            </p>
                        </div>
                    )}
                    {dataSource.length > 0 && (
                        <Section
                            icon={false}
                            name={props.selected?.name}
                            separator={false}
                            spaces={spaces}
                        >
                            {dataSource
                                .filter((it) => it.name)
                                .sort(sort.asc)
                                .map((it) => (
                                    <Module
                                        description={it.description}
                                        key={it.id}
                                        label={it.name}
                                        onClick={props.onClick}
                                        options={it.options}
                                        testid={it.name}
                                        value={it}
                                    />
                                ))}
                        </Section>
                    )}
                </div>
            </div>
        </div>
    );
}
